import { PileType } from '../types/index.js';

export interface DragDropCallbacks {
  onMove: (fromPile: PileType, fromIndex: number, toPile: PileType, toIndex: number, cardCount: number) => boolean;
  onStockClick: () => void;
  onCardDoubleClick: (pile: PileType, index: number) => boolean;
}

export class DragDropHandler {
  private callbacks: DragDropCallbacks;
  private draggedElement: HTMLElement | null = null;
  private draggedCards: HTMLElement[] = [];
  private sourceInfo: { pile: PileType; index: number; cardIndex: number } | null = null;
  private offsetX: number = 0;
  private offsetY: number = 0;
  private activeTouch: Touch | null = null;
  private lastTapTime: number = 0;
  private lastTapTarget: HTMLElement | null = null;
  private readonly DOUBLE_TAP_DELAY = 300; // ms

  constructor(callbacks: DragDropCallbacks) {
    this.callbacks = callbacks;
    this.initializeEventListeners();
  }

  /**
   * Initialize all event listeners for drag and drop
   */
  private initializeEventListeners(): void {
    // Mouse events
    document.addEventListener('mousedown', this.onMouseDown.bind(this));
    document.addEventListener('mousemove', this.onMouseMove.bind(this));
    document.addEventListener('mouseup', this.onMouseUp.bind(this));

    // Touch events (mobile)
    document.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false });
    document.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
    document.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: false });
    document.addEventListener('touchcancel', this.onTouchCancel.bind(this), { passive: false });

    // Stock pile click (for drawing cards)
    const stockPile = document.getElementById('stock-pile');
    if (stockPile) {
      stockPile.addEventListener('click', this.onStockClick.bind(this));
      stockPile.addEventListener('touchend', this.onStockTouchEnd.bind(this));
    }
  }

  /**
   * Handle stock pile click/tap
   */
  private onStockClick(e: MouseEvent): void {
    e.stopPropagation();
    this.callbacks.onStockClick();
  }

  /**
   * Handle stock pile touch end (separate from drag)
   */
  private onStockTouchEnd(e: TouchEvent): void {
    if (!this.draggedElement) {
      e.preventDefault();
      e.stopPropagation();
      this.callbacks.onStockClick();
    }
  }

  /**
   * Check if this is a double tap/click
   */
  private isDoubleTap(card: HTMLElement): boolean {
    const now = Date.now();
    const isDouble =
      this.lastTapTarget === card &&
      (now - this.lastTapTime) < this.DOUBLE_TAP_DELAY;

    this.lastTapTime = now;
    this.lastTapTarget = card;

    return isDouble;
  }

  /**
   * Handle double click on card (auto-move to foundation)
   */
  private handleDoubleClick(card: HTMLElement): boolean {
    const sourceInfo = this.getCardSourceInfo(card);
    if (!sourceInfo) {
      return false;
    }

    // Try to auto-move to foundation
    const success = this.callbacks.onCardDoubleClick(sourceInfo.pile, sourceInfo.index);

    if (success) {
      // Haptic feedback on success
      this.vibrate(20);
    }

    return success;
  }

  /**
   * Mouse down - start drag
   */
  private onMouseDown(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    const card = target.closest('.card') as HTMLElement;

    if (!card || card.classList.contains('card-back')) {
      return;
    }

    // Don't drag cards in stock or waste that aren't the top card
    if (this.shouldPreventDrag(card)) {
      return;
    }

    // Check for double click
    if (this.isDoubleTap(card)) {
      e.preventDefault();
      this.handleDoubleClick(card);
      return;
    }

    this.startDrag(card, e.clientX, e.clientY);
  }

  /**
   * Touch start - start drag on mobile
   */
  private onTouchStart(e: TouchEvent): void {
    if (e.touches.length !== 1) {
      return;
    }

    const touch = e.touches[0];
    const target = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement;
    const card = target?.closest('.card') as HTMLElement;

    if (!card || card.classList.contains('card-back')) {
      return;
    }

    // Don't drag cards in stock
    if (this.shouldPreventDrag(card)) {
      return;
    }

    // Check for double tap
    if (this.isDoubleTap(card)) {
      e.preventDefault();
      this.handleDoubleClick(card);
      return;
    }

    e.preventDefault(); // Prevent scrolling
    this.activeTouch = touch;
    this.startDrag(card, touch.clientX, touch.clientY);

    // Haptic feedback
    this.vibrate(10);
  }

  /**
   * Check if drag should be prevented for this card
   */
  private shouldPreventDrag(card: HTMLElement): boolean {
    // Can't drag from stock
    if (card.closest('#stock-pile')) {
      return true;
    }

    // Can only drag top card from waste
    if (card.closest('#waste-pile')) {
      const wastePile = document.getElementById('waste-pile')!;
      const cards = Array.from(wastePile.querySelectorAll('.card'));
      return card !== cards[cards.length - 1];
    }

    return false;
  }

  /**
   * Start dragging a card
   */
  private startDrag(card: HTMLElement, clientX: number, clientY: number): void {
    const rect = card.getBoundingClientRect();
    this.offsetX = clientX - rect.left;
    this.offsetY = clientY - rect.top;

    // Determine source pile and index
    this.sourceInfo = this.getCardSourceInfo(card);
    if (!this.sourceInfo) {
      return;
    }

    // For tableau, collect all cards from this one downward
    if (this.sourceInfo.pile === PileType.TABLEAU) {
      this.draggedCards = this.getTableauCardSequence(card);
    } else {
      this.draggedCards = [card];
    }

    this.draggedElement = card;

    // Visual feedback
    this.draggedCards.forEach(c => c.classList.add('card-dragging'));
  }

  /**
   * Get all cards in tableau from given card to bottom
   */
  private getTableauCardSequence(card: HTMLElement): HTMLElement[] {
    const pile = card.closest('.tableau-pile') as HTMLElement;
    if (!pile) {
      return [card];
    }

    const allCards = Array.from(pile.querySelectorAll('.card')) as HTMLElement[];
    const startIndex = allCards.indexOf(card);
    return allCards.slice(startIndex);
  }

  /**
   * Mouse move - update drag position
   */
  private onMouseMove(e: MouseEvent): void {
    if (!this.draggedElement) {
      return;
    }

    this.updateDragPosition(e.clientX, e.clientY);
    this.updateDropTargetHighlight(e.clientX, e.clientY);
  }

  /**
   * Touch move - update drag position on mobile
   */
  private onTouchMove(e: TouchEvent): void {
    if (!this.draggedElement || !this.activeTouch) {
      return;
    }

    e.preventDefault(); // Prevent scrolling

    const touch = e.touches[0];
    this.updateDragPosition(touch.clientX, touch.clientY);
    this.updateDropTargetHighlight(touch.clientX, touch.clientY);
  }

  /**
   * Update the position of dragged cards
   */
  private updateDragPosition(clientX: number, clientY: number): void {
    if (!this.draggedElement) {
      return;
    }

    const x = clientX - this.offsetX;
    const y = clientY - this.offsetY;

    this.draggedCards.forEach((card, index) => {
      card.style.position = 'fixed';
      card.style.left = `${x}px`;
      card.style.top = `${y + index * 25}px`; // Stack dragged cards
      card.style.zIndex = '1000';
      card.style.pointerEvents = 'none';
    });
  }

  /**
   * Update drop target highlighting
   */
  private updateDropTargetHighlight(clientX: number, clientY: number): void {
    // Clear previous highlights
    document.querySelectorAll('.drop-target-valid, .drop-target-invalid').forEach(el => {
      el.classList.remove('drop-target-valid', 'drop-target-invalid');
    });

    // Find element under cursor/finger (excluding dragged cards)
    const elementUnder = document.elementFromPoint(clientX, clientY);
    if (!elementUnder) {
      return;
    }

    const pile = elementUnder.closest('.pile') as HTMLElement;
    if (!pile) {
      return;
    }

    // Check if this is a valid drop target
    const isValid = this.isValidDropTarget(pile);
    pile.classList.add(isValid ? 'drop-target-valid' : 'drop-target-invalid');
  }

  /**
   * Check if pile is a valid drop target (simplified check)
   */
  private isValidDropTarget(pile: HTMLElement): boolean {
    // This is a simplified check - actual validation happens in GameState
    return pile.classList.contains('foundation-pile') || pile.classList.contains('tableau-pile');
  }

  /**
   * Mouse up - end drag
   */
  private onMouseUp(e: MouseEvent): void {
    if (!this.draggedElement) {
      return;
    }

    this.endDrag(e.clientX, e.clientY);
  }

  /**
   * Touch end - end drag on mobile
   */
  private onTouchEnd(e: TouchEvent): void {
    if (!this.draggedElement || !this.activeTouch) {
      return;
    }

    e.preventDefault();

    const touch = e.changedTouches[0];
    this.endDrag(touch.clientX, touch.clientY);
    this.activeTouch = null;
  }

  /**
   * Touch cancel - cancel drag
   */
  private onTouchCancel(_e: TouchEvent): void {
    if (!this.draggedElement) {
      return;
    }

    this.cancelDrag();
    this.activeTouch = null;
  }

  /**
   * End drag and attempt to drop
   */
  private endDrag(clientX: number, clientY: number): void {
    if (!this.draggedElement || !this.sourceInfo) {
      return;
    }

    // Find drop target
    const elementUnder = document.elementFromPoint(clientX, clientY);
    const targetPile = elementUnder?.closest('.pile') as HTMLElement;

    let success = false;

    if (targetPile) {
      const targetInfo = this.getPileInfo(targetPile);
      if (targetInfo) {
        // Attempt the move
        success = this.callbacks.onMove(
          this.sourceInfo.pile,
          this.sourceInfo.index,
          targetInfo.pile,
          targetInfo.index,
          this.draggedCards.length
        );

        if (success) {
          // Haptic feedback on success
          this.vibrate(20);
        }
      }
    }

    if (!success) {
      // Return cards to original position
      this.returnToOriginalPosition();
    }

    this.cleanup();
  }

  /**
   * Cancel drag and return to original position
   */
  private cancelDrag(): void {
    this.returnToOriginalPosition();
    this.cleanup();
  }

  /**
   * Return dragged cards to original position
   */
  private returnToOriginalPosition(): void {
    this.draggedCards.forEach(card => {
      card.style.position = '';
      card.style.left = '';
      card.style.top = '';
      card.style.zIndex = '';
      card.style.pointerEvents = '';
    });
  }

  /**
   * Clean up after drag
   */
  private cleanup(): void {
    this.draggedCards.forEach(card => {
      card.classList.remove('card-dragging');
      card.style.position = '';
      card.style.left = '';
      card.style.top = '';
      card.style.zIndex = '';
      card.style.pointerEvents = '';
    });

    document.querySelectorAll('.drop-target-valid, .drop-target-invalid').forEach(el => {
      el.classList.remove('drop-target-valid', 'drop-target-invalid');
    });

    this.draggedElement = null;
    this.draggedCards = [];
    this.sourceInfo = null;
  }

  /**
   * Get source info for a card
   */
  private getCardSourceInfo(card: HTMLElement): { pile: PileType; index: number; cardIndex: number } | null {
    if (card.closest('#waste-pile')) {
      return { pile: PileType.WASTE, index: 0, cardIndex: 0 };
    }

    const foundationPile = card.closest('.foundation-pile') as HTMLElement;
    if (foundationPile) {
      const index = parseInt(foundationPile.dataset.index || '0');
      return { pile: PileType.FOUNDATION, index, cardIndex: 0 };
    }

    const tableauPile = card.closest('.tableau-pile') as HTMLElement;
    if (tableauPile) {
      const index = parseInt(card.dataset.tableauIndex || '0');
      const cardIndex = parseInt(card.dataset.cardIndexInPile || '0');
      return { pile: PileType.TABLEAU, index, cardIndex };
    }

    return null;
  }

  /**
   * Get pile info from pile element
   */
  private getPileInfo(pile: HTMLElement): { pile: PileType; index: number } | null {
    if (pile.classList.contains('foundation-pile')) {
      const index = parseInt(pile.dataset.index || '0');
      return { pile: PileType.FOUNDATION, index };
    }

    if (pile.classList.contains('tableau-pile')) {
      const index = parseInt(pile.dataset.index || '0');
      return { pile: PileType.TABLEAU, index };
    }

    return null;
  }

  /**
   * Vibrate device (haptic feedback)
   */
  private vibrate(duration: number): void {
    if ('vibrate' in navigator) {
      navigator.vibrate(duration);
    }
  }
}
